import type { AiChatMessage } from './types';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';

import { CONFIG } from 'src/global-config';

import { Iconify } from 'src/components/iconify';

// ----------------------------------------------------------------------

type Props = {
  message: AiChatMessage;
};

export function AiChatMessageItem({ message }: Props) {
  const isUser = message.role === 'user';

  const renderAvatar = () => (
    <Avatar
      src={isUser ? undefined : `${CONFIG.assetsDir}/logo/lumi.png`}
      sx={{
        width: 32,
        height: 32,
        flexShrink: 0,
        ...(isUser && {
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
        }),
      }}
    >
      {isUser ? 'U' : null}
    </Avatar>
  );

  const renderAttachments = () => {
    if (!message.attachments?.length) return null;

    return (
      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
        {message.attachments.map((attachment) => {
          const isImage = attachment.type.startsWith('image/');
          const isPdf = attachment.type === 'application/pdf';

          // Render image preview
          if (isImage && attachment.preview) {
            return (
              <Box
                key={attachment.id}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                }}
              >
                <Box
                  component="img"
                  src={attachment.preview}
                  alt={attachment.name}
                  sx={{
                    maxWidth: 200,
                    maxHeight: 150,
                    objectFit: 'contain',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: isUser ? 'primary.light' : 'divider',
                  }}
                />
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {attachment.name}
                </Typography>
              </Box>
            );
          }

          // Render file chip for PDFs and other files
          return (
            <Box
              key={attachment.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.5,
                py: 0.5,
                bgcolor: isUser ? 'primary.light' : 'action.hover',
                borderRadius: 1,
                typography: 'caption',
              }}
            >
              <Iconify
                icon="solar:file-text-bold"
                width={16}
                sx={{ color: isPdf ? 'error.main' : undefined }}
              />
              {attachment.name}
            </Box>
          );
        })}
      </Stack>
    );
  };

  const renderContent = () => (
    <Box
      sx={{
        p: 2,
        minWidth: 48,
        maxWidth: 640,
        borderRadius: 2,
        bgcolor: isUser ? 'primary.lighter' : 'background.neutral',
        ...(isUser && {
          color: 'primary.darker',
        }),
      }}
    >
      {message.content && (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {message.content}
        </Typography>
      )}
      {renderAttachments()}
    </Box>
  );

  return (
    <Stack direction="row" justifyContent={isUser ? 'flex-end' : 'flex-start'} sx={{ mb: 3 }}>
      <Stack
        direction="row"
        alignItems="flex-start"
        spacing={1.5}
        sx={{
          maxWidth: '85%',
          ...(isUser && {
            flexDirection: 'row-reverse',
          }),
        }}
      >
        {renderAvatar()}

        <Box>
          <Typography
            variant="caption"
            sx={{
              mb: 0.5,
              display: 'block',
              color: 'text.disabled',
              fontWeight: 500,
              ...(isUser && { textAlign: 'right' }),
            }}
          >
            {isUser ? 'You' : 'Lumi AI'}
          </Typography>
          {renderContent()}
        </Box>
      </Stack>
    </Stack>
  );
}
